// Product-shaped and beauty award trophies. Targets (coordinator research,
// 2026-11-03): custom shaped trophy, lipstick trophy, perfume bottle trophy,
// beauty awards trophy, influencer awards trophy, custom 3d printed trophy;
// "3d printed trophy" (1,300) stays owned by /services/custom-trophy/, which
// this page links up to. Prices and turnaround are the published trophy
// terms (src/data/custom-trophy.js, .seo/truth.md); examples are anonymized
// owner facts (2026-11-03) or clients already named on /services/custom-trophy/.
const { pricing, photos, photoUrl } = require('../custom-trophy')

const trophyPhoto = (name, caption, note) => ({
  src: (width) => photoUrl(photos[name], width),
  width: 1500,
  height: Math.round(1500 / photos[name].ratio),
  alt: photos[name].alt,
  caption,
  note,
})

const productShapedTrophies = {
  key: 'productShapedTrophies',
  path: '/services/custom-trophy/product-shaped-trophies/',
  quoteHash: '#quote',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  title: 'Custom Shaped Trophies: Lipstick, Bottle & Product Awards',
  description: `Custom shaped trophies made like your product: lipstick, perfume bottle, compact, can or cup, 3D printed for beauty and creator awards. From $${pricing.from}.`,
  crumbs: [['Custom trophies', '/services/custom-trophy/']],
  crumb: 'Product-shaped trophies',
  eyebrow: 'PRODUCT-SHAPED TROPHIES · BEAUTY AWARDS',
  heading: ['Trophies shaped', 'like your product.'],
  answer: `PrintAWorld makes custom shaped trophies in New York: your hero product, a lipstick, perfume bottle, compact, jar, can or cup, sculpted in 3D as the award itself for creator programs, beauty and influencer award nights, and sales and retailer awards. We model it from your product or packaging file, 3D print it in resin, paint it or metal-plate the details, and add name plates. Custom trophies start at $${pricing.from} each plus a one-time $${pricing.modelingFee} modeling fee for most projects, with no minimum. Standard turnaround is 4–6 weeks (6–8 weeks for longer, complex or plated pieces), with rush in 1–2 weeks for many projects.`,
  cta: 'Get a trophy quote',
  heroLink: 'Ideas',
  heroPhoto: trophyPhoto(
    'gold',
    'Gold-plated soccer cleats',
    'A product made into a trophy in our studio'
  ),
  sections: [
    {
      id: 'ideas',
      jump: 'Ideas',
      eyebrow: 'IDEAS',
      title: [
        'Lipstick trophies, bottle trophies',
        'and other product awards.',
      ],
      intro:
        'The product people already love, turned into the award they take home. It works for any brand with a recognizable shape.',
      rows: [
        {
          title: 'Lipstick trophies',
          body: 'A lipstick bullet and case at trophy size, painted in the brand shade with plated details.',
        },
        {
          title: 'Perfume bottle trophies',
          body: 'The bottle as the award, painted or plated, with the winner’s name on the base.',
        },
        {
          title: 'Compacts, jars and palettes',
          body: 'Skincare jars, compacts and palettes made into awards for a creator program or a retailer’s brand partners.',
        },
        {
          title: 'Cans, cups and bottles',
          body: 'Food and drink brands: the can, cup or bottle in gold, like the drink-cup trophies we made for a restaurant brand’s conference.',
        },
        {
          title: 'Characters and mascots',
          body: 'A brand character or team mascot as a set of awards, like the mascot awards we make for tech companies.',
        },
      ],
    },
    {
      id: 'occasions',
      jump: 'Occasions',
      eyebrow: 'OCCASIONS',
      title: [
        'Beauty awards, creator programs',
        'and influencer award nights.',
      ],
      rows: [
        {
          title: 'Creator and influencer programs',
          body: 'A year-end award for the creators in your program, with each name on the base.',
        },
        {
          title: 'Beauty award nights',
          body: 'Category trophies for a beauty awards event, all in the same product shape.',
        },
        {
          title: 'Retailer and brand-partner awards',
          body: 'Awards a retailer gives its brands, or a brand gives its top stores.',
        },
        {
          title: 'Sales awards and conferences',
          body: 'Top sellers and regional winners, presented on stage at the annual meeting.',
        },
      ],
    },
    {
      id: 'past-projects',
      jump: 'Past projects',
      eyebrow: 'PAST PROJECTS',
      title: ['Product-shaped trophies', 'we’ve made.'],
      intro:
        'Described without the brand, except where the client is already on our trophy page.',
      link: ['See the trophy gallery', '/gallery/custom-trophy-and-awards/'],
      rows: [
        {
          title: '10 lipstick trophies',
          body: 'Ten 12-inch lipstick-shaped trophies in painted resin with metal-plated details, for a beauty retailer’s creator-program year-end event, about $1,200 each.',
        },
        {
          title: 'Gold drink-cup trophies',
          body: 'Gold cup trophies shaped like a restaurant brand’s drink cup, with gold plates, for its conference.',
        },
        {
          title: 'Mascot trophies for tech teams',
          body: 'A tech company’s mascot as a set of gold-painted awards with category plates.',
        },
        {
          title: 'Formic’s robot arm',
          body: 'A desk-size model of the client’s robot arm, as the award.',
        },
      ],
    },
    {
      id: 'studio-work',
      jump: 'Our work',
      eyebrow: 'FROM OUR STUDIO',
      title: ['Products and mascots', 'made into awards.'],
      gallery: [
        {
          photo: trophyPhoto('rcCatsSet'),
          caption: 'A team mascot as a set of awards',
          layout: 'feature',
        },
        {
          photo: trophyPhoto('goGetter'),
          caption: 'Dunkin’ Go Getter awards',
          layout: 'wide',
        },
        {
          photo: trophyPhoto('formicArm'),
          caption: 'Formic’s robot arm, as the award',
        },
        {
          photo: trophyPhoto('goldTowers'),
          caption: 'Gold-plated tower awards',
        },
      ],
    },
    {
      id: 'at-a-glance',
      jump: 'Price',
      eyebrow: 'AT A GLANCE',
      title: ['Product trophy price,', 'size and turnaround.'],
      rows: [
        {
          title: 'Price',
          body: `From $${pricing.from} per trophy. Painted resin usually costs $250–$750; plated, gold or complex multi-color pieces run $750–$2,000 or more; 16 inches and taller start at $1,500.`,
        },
        {
          title: 'Design',
          body: `A free concept mockup first, then a one-time 3D modeling fee of $${pricing.modelingFee} for most projects, paid once however many you order.`,
        },
        {
          title: 'Finish',
          body: 'Painted to your shades, or metal plated: a real metal layer over the resin, not solid metal.',
        },
        {
          title: 'Turnaround',
          body: '4–6 weeks standard; 6–8 weeks for longer, complex or plated pieces (plating runs 6–8 weeks after the model is approved). Rush in 1–2 weeks is possible for many projects, depending on the design, budget and where it ships.',
        },
        {
          title: 'Minimum',
          body: 'None. One award or a set with every winner’s name.',
        },
      ],
    },
  ],
  process: {
    title: ['From your product', 'to the award on stage.'],
    timing: ['4–6 weeks', '6–8 if plated; rush in 1–2 weeks for many'],
    steps: [
      'Send product photos or the packaging file, how many awards and the event date.',
      'We send a free concept mockup.',
      'We model the trophy in 3D and send it for approval, with the plates laid out.',
      'We 3D print it in resin, sand it, and paint or plate it.',
      'We engrave the name plates, mount each award, and ship it or hold it for pickup.',
    ],
    links: [
      ['Custom 3D printed trophies', '/services/custom-trophy/'],
      [
        'Logo trophies and corporate awards',
        '/services/custom-trophy/logo-trophies/',
      ],
      ['Custom props and giant replicas', '/services/custom-props/'],
    ],
  },
  reviews: ['custom-trophies', 'laser-engraving'],
  faqTitle: ['Product-shaped trophy questions,', 'answered.'],
  faqs: [
    {
      question: 'How much does a custom shaped trophy cost?',
      answer: `Custom trophies start at $${pricing.from} each. Painted resin usually costs $250–$750, and plated or complex pieces run $750–$2,000 or more. Our 12-inch lipstick trophies with plated details were about $1,200 each. There’s a one-time $${pricing.modelingFee} 3D modeling fee for most projects.`,
    },
    {
      question:
        'Can you make a trophy shaped like our lipstick or perfume bottle?',
      answer:
        'Yes. Send product photos or the packaging file. We model the product in 3D, add a base and name plate, and you approve the model before we print it.',
    },
    {
      question:
        'Who makes 3D printed trophies for influencer and creator awards?',
      answer:
        'PrintAWorld, in New York, 3D prints custom trophies for creator programs, beauty awards and brand events, including 10 lipstick-shaped trophies for a beauty retailer’s creator-program event. We ship to all 50 states.',
    },
    {
      question: 'Are gold and chrome trophies solid metal?',
      answer:
        'No. The trophies are resin. A gold, silver or chrome look is paint or metal plating, a real metal layer over the print.',
    },
    {
      question: 'How long do product-shaped trophies take?',
      answer:
        'Standard turnaround is 4–6 weeks, and 6–8 weeks for longer, complex or plated pieces, like our lipstick trophies with plated details. Rush in 1–2 weeks is possible for many projects. Tell us your event date with the request.',
    },
    {
      question: 'Is there a minimum order?',
      answer:
        'No. One award or a full set; the 3D model is paid once, then each trophy is printed, finished and engraved.',
    },
  ],
  final: {
    eyebrow: 'START WITH THE PRODUCT',
    title: ['Which product', 'becomes the award?'],
    body: 'Send product photos or the packaging file, how many awards and your event date. We’ll write back with a free mockup and a price.',
  },
  sendList: [
    'Product photos or the packaging file',
    'How many awards, and the categories',
    'Names for the plates, if you have them',
    'Your event date and delivery ZIP',
  ],
  schema: {
    name: 'Product-shaped custom trophies',
    alternateName: [
      'Custom shaped trophies',
      'Lipstick trophies',
      'Perfume bottle trophies',
      'Beauty award trophies',
      'Influencer award trophies',
    ],
    serviceType: 'Custom trophy fabrication',
    minPrice: pricing.from,
  },
}

module.exports = { productShapedTrophies }
